import React, { useState } from "react";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Define size options for the Avatar
type AvatarSize = "tiny" | "xsmall" | "small" | "medium" | "mlarge" | "large";

// Props for the Avatar component
interface AvatarProps {
  firstName: string; // User's first name
  lastName?: string | null; // User's last name (optional)
  profileImageUrl?: string | null | undefined; // Optional profile image URL
  size?: AvatarSize; // Size of the avatar (default is "medium")
  className?: string; // Additional custom styles
  loading?: boolean; // Loading state
}

const sizeClasses = {
  tiny: "w-6 h-6 text-xs", // 24px
  xsmall: "w-8 h-8 text-xs", // 32px
  small: "w-10 h-10 text-xs", // 40px
  medium: "w-12 h-12 text-sm", // 48px
  mlarge: "w-16 h-16 text-md", // 64px
  large: "w-20 h-20 text-lg", // 80px
};

const skeletonSizes = {
  tiny: 24,
  xsmall: 32,
  small: 40,
  medium: 48,
  mlarge: 64,
  large: 80,
};

const Avatar: React.FC<AvatarProps> = ({
  firstName,
  lastName: lastNameProp,
  profileImageUrl,
  size = "medium",
  className = "",
  loading = false,
}) => {
  const lastName = lastNameProp || "";
  const [imageLoading, setImageLoading] = useState(true);

  // Generate fallback initials
  const getInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName?.[0]?.toUpperCase();
    const lastInitial = lastName[0]?.toUpperCase();
    return lastInitial ? `${firstInitial}${lastInitial}` : firstInitial;
  };

  const initials = getInitials(firstName, lastName);

  if (loading) {
    return <Skeleton circle width={skeletonSizes[size]} height={skeletonSizes[size]} className={className} />;
  }

  return (
    <div className={`relative inline-flex items-center justify-center rounded-full flex-shrink-0 aspect-square ${sizeClasses[size]} ${className}`}>
      {profileImageUrl ? (
        <>
          {imageLoading && (
            <Skeleton 
              circle 
              width={skeletonSizes[size]} 
              height={skeletonSizes[size]} 
              className="absolute inset-0 rounded-full"
            />
          )}
          <div className="w-full h-full overflow-hidden rounded-full">
            <img
              src={profileImageUrl}
              alt={`${firstName} ${lastName}`}
              className={`w-full h-full object-cover ${imageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
              onLoad={() => setImageLoading(false)}
            />
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center rounded-full bg-primary-base text-white font-bold">
          {initials}
        </div>
      )}
    </div>
  );
};

export default Avatar;